import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { FlowerOfLife, CircuitBackground, RuneDivider } from "@/components/SacredGeometry";
import { ArrowRight } from "lucide-react";
const HERO_BG = "https://images.unsplash.com/photo-1687506765495-88a298ab900b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxuaWdodCUyMHNreSUyMHN0YXJzfGVufDB8fHx8MTc3NzcxMTk3NHww&ixlib=rb-4.1.0&q=85";
const TECH_BG = "https://images.unsplash.com/photo-1651340608985-d25cc73156e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxjaXJjdWl0JTIwYm9hcmQlMjBkYXJrJTIwYmx1ZXxlbnwwfHx8fDE3Nzc3MTE5NzR8MA&ixlib=rb-4.1.0&q=85";
const GOLD_GEO = "https://images.unsplash.com/photo-1633188985850-41c3c2ba9c2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxnb2xkJTIwZ2VvbWV0cnklMjBhYnN0cmFjdHxlbnwwfHx8fDE3Nzc3MTE5NzR8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  const { t, locale } = useLang();
  const isFr = locale === "fr";
  const soonLabel = isFr ? "Bientôt" : "Soon";
  const pillars = [
    { key: "geobiologie", to: "/geobiologie" },
    { key: "magnetisme", to: "/magnetisme" },
    { key: "cuisine", to: "/cuisine-vivante" },
  ];

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-12 pb-32 lg:pt-16 lg:pb-44">
        <div
          className="absolute inset-0 opacity-40"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div
          className="absolute inset-0 opacity-15 mix-blend-screen pointer-events-none"
          style={{ backgroundImage: `url(${TECH_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-[#0F1A3A]/70" />
        <CircuitBackground className="absolute inset-0 w-full h-full" />

        <div className="absolute right-[-80px] top-[40px] opacity-50 animate-pulse-glow hidden md:block">
          <div className="animate-slow-spin">
            <FlowerOfLife size={520} color="#D4AF37" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p
            initial="hidden" animate="show" variants={fadeUp}
            className="label-overline mb-6"
          >
            {t("home.tagline")}
          </motion.p>

          <motion.h1
            initial="hidden" animate="show" variants={fadeUp}
            className="font-display text-[clamp(3rem,9vw,7.5rem)] leading-[0.95] tracking-tight text-amber-glow"
            data-testid="hero-title"
          >
            FACOW<span className="text-cyan-glow">360</span>
          </motion.h1>

          <motion.div
            initial="hidden" animate="show" variants={fadeUp}
            transition={{ delay: 0.2 }}
            className="mt-6 max-w-2xl text-slate-300 text-base lg:text-lg leading-relaxed space-y-5"
            data-testid="hero-presentation"
          >
            <p>
              {isFr
                ? "La méthode FACOW360 repose sur des services professionnels strictement dissociés :"
                : "The FACOW360 method rests on strictly separated professional services:"}
            </p>
            <ul className="space-y-2.5 text-sm lg:text-base">
              <li className="flex gap-2.5">
                <span className="text-[#D4AF37]">•</span>
                <span><span className="text-[#D4AF37] font-semibold tracking-wide">{isFr ? "GÉOBIOLOGIE" : "GEOBIOLOGY"} :</span>{" "}
                  {isFr
                    ? "Analyse de l'habitat (détection des perturbations et harmonisation des lieux de vie)."
                    : "Habitat analysis (detection of disturbances and harmonisation of living spaces)."}</span>
              </li>
              <li className="flex gap-2.5">
                <span className="text-[#D4AF37]">•</span>
                <span><span className="text-[#D4AF37] font-semibold tracking-wide">{isFr ? "MAGNÉTISME" : "MAGNETISM"} :</span>{" "}
                  {isFr
                    ? "Rééquilibrage énergétique (libération des tensions et harmonisation des flux)."
                    : "Energetic rebalancing (release of tensions and harmonisation of flows)."}</span>
              </li>
              <li className="flex gap-2.5">
                <span className="text-[#D4AF37]">•</span>
                <span><span className="text-[#D4AF37] font-semibold tracking-wide">{isFr ? "CUISINE VIVANTE" : "LIVING CUISINE"} :</span>{" "}
                  {isFr
                    ? "Conseil et accompagnement nutritionnel personnalisé."
                    : "Personalised nutritional advice and support."}</span>
              </li>
            </ul>
            <p>
              {isFr
                ? "L'approche nutritionnelle s'articule rigoureusement autour de 5 piliers :"
                : "The nutritional approach is rigorously articulated around 5 pillars:"}
            </p>
            <ol className="space-y-2 text-sm lg:text-base list-none">
              {(isFr
                ? [
                    ["Système des Groupes Sanguins", "compatibilités biologiques"],
                    ["Ayurvéda", "constitution et profil énergétique"],
                    ["Naturopathie", "régénération naturelle et hygiène de vie"],
                    ["Chronobiologie", "rythmes biologiques du corps"],
                    ["Jeûne", "repos digestif et détoxification"],
                  ]
                : [
                    ["Blood Group System", "biological compatibilities"],
                    ["Ayurveda", "constitution and energetic profile"],
                    ["Naturopathy", "natural regeneration and healthy living"],
                    ["Chronobiology", "the body's biological rhythms"],
                    ["Fasting", "digestive rest and detoxification"],
                  ]
              ).map(([title, desc], i) => (
                <li key={title} className="flex gap-2.5">
                  <span className="font-mono-tech text-[#00F0FF]">{i + 1}.</span>
                  <span><span className="text-slate-100 font-medium">{title}</span> ({desc})</span>
                </li>
              ))}
            </ol>
          </motion.div>

          <motion.div
            initial="hidden" animate="show" variants={fadeUp}
            transition={{ delay: 0.4 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link to="/contact" className="btn-primary" data-testid="hero-cta-contact">
              {t("nav.contact")}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Two pillars block: IA & Tradition — removed on user request */}

      <RuneDivider />

      {/* Five Pillars */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="pillars-section">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="label-overline mb-4">— 3 Piliers</p>
          <h2 className="font-display text-3xl lg:text-5xl text-amber-glow mb-4 tracking-tight">
            {t("home.pillars_title")}
          </h2>
          <p className="text-slate-400">{t("home.pillars_sub")}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillars.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05 }}
            >
              <Link
                to={p.to}
                className="block glass glow-border rounded-2xl p-7 h-full hover:translate-y-[-2px] transition-transform relative"
                data-testid={`pillar-${p.key}`}
              >
                {p.comingSoon && (
                  <span
                    className="absolute top-4 right-4 px-2 py-1 rounded-full text-[9px] font-mono-tech tracking-[0.2em] uppercase border border-[#D4AF37]/40 bg-[#D4AF37]/15 text-[#D4AF37]"
                    data-testid={`pillar-${p.key}-soon`}
                  >
                    {soonLabel}
                  </span>
                )}
                <p className="label-overline mb-3">{`0${i + 1}`}</p>
                <h3 className="font-display text-xl text-amber-glow mb-3">{t(`pillars.${p.key}.title`)}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{t(`pillars.${p.key}.short`)}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-[#00F0FF]">
                  {p.comingSoon ? (isFr ? "Prochainement" : "Coming soon") : (isFr ? "Découvrir" : "Discover")} <ArrowRight size={12} />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Visual Tradition strip */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${GOLD_GEO})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1A3A] via-[#0F1A3A]/60 to-[#0F1A3A]" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="label-overline mb-4">— Conviction</p>
          <h2 className="font-display text-2xl md:text-4xl leading-tight text-slate-100">
            « <span className="text-amber-glow">{t("home.bottom_phrase")}</span> »
          </h2>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/bilan" className="btn-gold" data-testid="closing-cta-bilan">
              {isFr ? "Démarrer mon Bilan (19 €)" : "Start my Assessment (€19)"}
            </Link>
            <Link to="/devis" className="btn-primary" data-testid="closing-cta-devis">
              Devis Géobiologie
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
